import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Briefcase, Users, MessageSquare, Calendar, Star, Mail,
  Upload, X, Loader2, Link as LinkIcon, Building2, MapPin, Clock,
  AlertCircle, CheckCircle, Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'business_leads', label: 'Business Leads', icon: Briefcase, description: 'Buying/selling opportunities, leads, RFQs' },
  { id: 'partnerships', label: 'Partnerships', icon: Users, description: 'JV, distribution, licensing opportunities' },
  { id: 'consulting_requests', label: 'Consulting Requests', icon: MessageSquare, description: 'Advisory, consulting, expertise needed' },
  { id: 'invitations', label: 'Invitations', icon: Mail, description: 'Meeting requests, facility visits, delegations' },
  { id: 'events', label: 'Events', icon: Calendar, description: 'Trade shows, conferences, webinars' },
  { id: 'others', label: 'Others', icon: Star, description: 'Anything else' }
];

export default function PostOpportunity() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [leadershipProfiles, setLeadershipProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    other_category: '',
    sector: '',
    geography: '',
    action_needed: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    linked_profile_id: '',
    linked_profile_type: '',
    deadline: '',
    attachments: []
  });
  
  // Fetch user and profiles
  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(userRes.data);
        
        // Pre-fill contact info
        setFormData(prev => ({
          ...prev,
          contact_name: userRes.data.name || '',
          contact_email: userRes.data.email || ''
        }));
        
        // Fetch user's profiles
        const [profilesRes, leadershipRes] = await Promise.all([
          axios.get(`${API_URL}/api/profiles/mine`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/my-leadership-profiles`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        
        setProfiles(Array.isArray(profilesRes.data) ? profilesRes.data : []);
        setLeadershipProfiles(Array.isArray(leadershipRes.data) ? leadershipRes.data : []);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login?redirect=/connect/post');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category) {
      alert('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      // Extract just the URLs from attachments
      const submitData = {
        ...formData,
        attachments: formData.attachments.map(a => a.url)
      };
      await axios.post(`${API_URL}/api/connect/opportunities`, submitData, { withCredentials: true });
      setSuccess(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit opportunity');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleProfileSelect = (profileId, profileType) => {
    setFormData({
      ...formData,
      linked_profile_id: profileId,
      linked_profile_type: profileType
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Opportunity Submitted!</h1>
          <p className="text-zinc-400 mb-6">
            Your opportunity has been submitted for review. Our team will review it and publish it shortly.
            You'll receive a notification once it's live.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/connect"
              className="bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-3 rounded-lg font-semibold"
            >
              Back to Board
            </Link>
            <button
              onClick={() => {
                setSuccess(false);
                setFormData({
                  title: '',
                  description: '',
                  category: '',
                  other_category: '',
                  sector: '',
                  geography: '',
                  action_needed: '',
                  contact_name: user?.name || '',
                  contact_email: user?.email || '',
                  contact_phone: '',
                  linked_profile_id: '',
                  linked_profile_type: '',
                  deadline: '',
                  attachments: []
                });
              }}
              className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold"
            >
              Post Another
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to="/connect" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Connect
          </Link>
          <h1 className="text-2xl font-bold">Post an Opportunity</h1>
          <p className="text-zinc-400 mt-1">
            Share a business opportunity with the BrandStage community
          </p>
        </div>
      </div>
      
      {/* Form */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium mb-3">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFormData({...formData, category: cat.id})}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.category === cat.id
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                        : 'border-zinc-700 hover:border-zinc-600 bg-zinc-900/50'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mb-2 ${formData.category === cat.id ? 'text-[#C41E3A]' : 'text-zinc-400'}`} />
                    <p className="font-medium">{cat.label}</p>
                    <p className="text-xs text-zinc-500 mt-1">{cat.description}</p>
                  </button>
                );
              })}
            </div>
            
            {formData.category === 'others' && (
              <input
                type="text"
                value={formData.other_category}
                onChange={(e) => setFormData({...formData, other_category: e.target.value})}
                placeholder="Please specify category..."
                className="mt-3 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3"
              />
            )}
          </div>
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., Looking for Distribution Partners in West Africa"
              required
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:border-[#C41E3A] focus:outline-none"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Describe the opportunity in detail. What are you looking for? What do you offer?"
              required
              rows={6}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:border-[#C41E3A] focus:outline-none"
            />
          </div>
          
          {/* Sector & Geography */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Sector / Industry</label>
              <input
                type="text"
                value={formData.sector}
                onChange={(e) => setFormData({...formData, sector: e.target.value})}
                placeholder="e.g., Pharmaceuticals, Textiles, IT"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Geography / Region</label>
              <div className="relative">
                <MapPin className="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={formData.geography}
                  onChange={(e) => setFormData({...formData, geography: e.target.value})}
                  placeholder="e.g., West Africa, Europe, Global"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-3"
                />
              </div>
            </div>
          </div>
          
          {/* Action Needed */}
          <div>
            <label className="block text-sm font-medium mb-2">
              <AlertCircle className="w-4 h-4 inline mr-1 text-amber-400" />
              Action Needed
            </label>
            <input
              type="text"
              value={formData.action_needed}
              onChange={(e) => setFormData({...formData, action_needed: e.target.value})}
              placeholder="e.g., Submit EOI by email, Schedule a call, Visit our booth"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3"
            />
          </div>
          
          {/* Deadline */}
          <div>
            <label className="block text-sm font-medium mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Deadline (optional)
            </label>
            <input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({...formData, deadline: e.target.value})}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3"
            />
          </div>
          
          {/* Link to Profile */}
          {(profiles.length > 0 || leadershipProfiles.length > 0) && (
            <div>
              <label className="block text-sm font-medium mb-2">
                <LinkIcon className="w-4 h-4 inline mr-1" />
                Link to Your Profile
              </label>
              <p className="text-sm text-zinc-500 mb-3">
                Linking your BrandStage profile adds credibility and lets responders learn more about you.
              </p>
              <div className="space-y-2">
                {/* Company Profiles */}
                {profiles.map(profile => (
                  <button
                    key={profile.id || profile._id}
                    type="button"
                    onClick={() => handleProfileSelect(profile.id || profile._id, 'company')}
                    className={`w-full p-3 rounded-lg border flex items-center gap-3 text-left transition-all ${
                      formData.linked_profile_id === (profile.id || profile._id)
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                        : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                    }`}
                  >
                    {profile.logo_url ? (
                      <img src={profile.logo_url} alt="" className="w-10 h-10 rounded-lg object-contain bg-white" />
                    ) : (
                      <div className="w-10 h-10 bg-zinc-700 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-zinc-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{profile.company_name}</p>
                      <p className="text-xs text-zinc-500">Company Profile</p>
                    </div>
                    {formData.linked_profile_id === (profile.id || profile._id) && (
                      <CheckCircle className="w-5 h-5 text-[#C41E3A] ml-auto" />
                    )}
                  </button>
                ))}
                
                {/* Leadership Profiles */}
                {leadershipProfiles.map(profile => (
                  <button
                    key={profile.id || profile._id}
                    type="button"
                    onClick={() => handleProfileSelect(profile.id || profile._id, 'leadership')}
                    className={`w-full p-3 rounded-lg border flex items-center gap-3 text-left transition-all ${
                      formData.linked_profile_id === (profile.id || profile._id)
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                        : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                    }`}
                  >
                    {profile.leader_photo ? (
                      <img src={profile.leader_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-zinc-400">
                          {profile.leader_name?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{profile.leader_name}</p>
                      <p className="text-xs text-zinc-500">Leadership Profile</p>
                    </div>
                    {formData.linked_profile_id === (profile.id || profile._id) && (
                      <CheckCircle className="w-5 h-5 text-[#C41E3A] ml-auto" />
                    )}
                  </button>
                ))}
                
                {/* Option to not link */}
                <button
                  type="button"
                  onClick={() => setFormData({...formData, linked_profile_id: '', linked_profile_type: ''})}
                  className={`w-full p-3 rounded-lg border flex items-center gap-3 text-left transition-all ${
                    !formData.linked_profile_id
                      ? 'border-zinc-600 bg-zinc-800'
                      : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                  }`}
                >
                  <div className="w-10 h-10 bg-zinc-700 rounded-lg flex items-center justify-center">
                    <X className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-400">Don't link a profile</p>
                    <p className="text-xs text-zinc-500">Post without linking to a profile</p>
                  </div>
                </button>
              </div>
            </div>
          )}
          
          {/* Attachments */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Attachments
            </h3>
            <p className="text-sm text-zinc-500 mb-4">
              Add images or PDFs to support your opportunity (max 5 files, 10MB each)
            </p>
            
            {/* Upload Area */}
            <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center hover:border-zinc-500 transition-colors">
              <input
                type="file"
                id="attachment-upload"
                accept="image/*,.pdf"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  if (formData.attachments.length + files.length > 5) {
                    alert('Maximum 5 attachments allowed');
                    return;
                  }
                  
                  for (const file of files) {
                    if (file.size > 10 * 1024 * 1024) {
                      alert(`${file.name} exceeds 10MB limit`);
                      continue;
                    }
                    
                    try {
                      const uploadFormData = new FormData();
                      uploadFormData.append('file', file);
                      
                      const res = await axios.post(
                        `${API_URL}/api/connect/opportunities/upload-attachment`,
                        uploadFormData,
                        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                      );
                      
                      if (res.data.success) {
                        setFormData(prev => ({
                          ...prev,
                          attachments: [...prev.attachments, {
                            url: res.data.url,
                            name: file.name,
                            type: file.type,
                            size: res.data.size
                          }]
                        }));
                      }
                    } catch (err) {
                      alert(`Failed to upload ${file.name}`);
                    }
                  }
                  e.target.value = '';
                }}
                className="hidden"
              />
              <label htmlFor="attachment-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                <p className="text-zinc-400">Click to upload or drag files here</p>
                <p className="text-xs text-zinc-600 mt-1">Images (JPEG, PNG, WebP) or PDF</p>
              </label>
            </div>
            
            {/* Uploaded Files List */}
            {formData.attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                {formData.attachments.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-zinc-800 rounded-lg p-3">
                    {file.type?.startsWith('image/') ? (
                      <img src={file.url} alt="" className="w-12 h-12 rounded object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-zinc-700 rounded flex items-center justify-center">
                        <span className="text-xs font-medium text-zinc-400">PDF</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-zinc-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        attachments: prev.attachments.filter((_, i) => i !== idx)
                      }))}
                      className="p-1 hover:bg-zinc-700 rounded"
                    >
                      <X className="w-4 h-4 text-zinc-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Contact Info */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h3 className="font-semibold mb-4">Contact Information</h3>
            <p className="text-sm text-zinc-500 mb-4">
              This will be visible to subscribers who view the full opportunity.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Contact Name</label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-zinc-400 mb-1">Contact Phone</label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="+91 98765 43210"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                />
              </div>
            </div>
          </div>
          
          {/* Submit */}
          <div className="flex gap-4">
            <Link
              to="/connect"
              className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-4 rounded-lg font-semibold text-center"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting || !formData.title || !formData.description || !formData.category}
              className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              data-testid="submit-opportunity-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit for Review'
              )}
            </button>
          </div>
          
          <p className="text-sm text-zinc-500 text-center">
            All opportunities are reviewed by our team before being published.
          </p>
        </form>
      </div>
    </div>
  );
}
